/**
 * Own base garments per colour or size (BLU-1522 S8), under the SKU
 * derivation tier of a product on the org's OWN blanks.
 *
 * The same pieces as the 3PL composer's grouped mode, so the two read as one
 * pattern: the quiet "Use different base garments for some colours or sizes"
 * link and the "Per colour / Per size" switch (`SplitControl`), a tick per
 * colour (or size) with its Base garment (`BlankValueMatchTable` with
 * `matchColumns={false}`: no blank values to match here), and "Assign base
 * garment". A base garment is one of the org's own product type codes, listed
 * as `CODE · store names` from its own code table (`ValuePicker`: a Select up
 * to 20, a searchable Combobox above). No SKU text anywhere.
 *
 * - Admins only (the save is admin + skus.manage on the server). An org on
 *   its own design IDs (BYO, not supported yet) sees nothing, or, when the
 *   product already has groups, only "Use one base garment for all". Hidden
 *   for a product with no type
 *   code, and a product with nothing to split. The row never mounts it in
 *   store SKU mode or for a product on a 3PL's blank.
 * - Saved: a short summary, each garment with its values, and "Change base
 *   garments". A saved choice the plan would hold back (`{ invalid: true }`,
 *   or colours, sizes or codes changed since) shows the plan's own words.
 * - Editing: Save sends the S2 shape once; "Use one base garment for all"
 *   then Save clears it. Synced colours (or sizes) are locked. Cancel drops
 *   the draft. Nothing is written to Shopify.
 * - Only one product type code: an empty state with a way to Code tables.
 *
 * The editor (and its save mutation) mounts only while open, so a row that is
 * only looked at makes no extra call.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2, Shirt } from 'lucide-react';
import { byoDesignIdsOn, type ProductSyncSettings } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NoteLine } from '@/components/shared/NoteLine';
import { cn } from '@/lib/utils';
import { HeldBackNote } from './HeldBackNote';
import { SplitControl } from './supply/SupplyComposer';
import { BlankValueMatchTable, ValuePicker, type BlankValueMatchRow } from './supply/BlankValueMatchTable';
import {
  isOwnEntryStale,
  isValidOwnEntry,
  ownGarmentOptions,
  ownSplittable,
  useOwnBlankGroups,
  type OwnBlankSaved,
  type OwnBlankSavedEntry,
  type OwnBlankValidEntry,
  type OwnGarmentOption,
} from '../hooks/useOwnBlankGroups';
import { GARMENT_GROUPS_MAX, garmentValueKey, productValues, type GarmentSplit } from '../lib/garment-groups';
import type { CodeTable, PreviewProduct } from '../lib/types';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

export interface OwnBlankGroupsPanelProps {
  product: PreviewProduct;
  buffer: ProductSyncSettingsBuffer;
  admin: boolean;
  /** Jump to the Code tables tab (the empty state's way to add a code). */
  onMapIt: (table: CodeTable, name?: string) => void;
}

export function OwnBlankGroupsPanel({ product, buffer, admin, onMapIt }: OwnBlankGroupsPanelProps) {
  // The SAVED settings: the code table the save checks garments against.
  const settings = buffer.serverSettings;
  if (!admin) return null;
  if (product.storeSku != null || product.supply != null) return null;
  if (!product.productType.code) return null;
  if (byoDesignIdsOn(settings)) {
    // BYO turned on after groups were saved: the plan holds the product back,
    // so the only way forward is back to one base garment.
    return product.ownBlankGroups ? <OwnBlankByoClear product={product} settings={settings} /> : null;
  }
  return <OwnBlankGroupsSection product={product} settings={settings} onMapIt={onMapIt} />;
}

/** BYO is on and the product still has groups: a held-back note and the one way back. */
function OwnBlankByoClear({ product, settings }: { product: PreviewProduct; settings: ProductSyncSettings }) {
  const { t } = useTranslation('productSync');
  const refusalId = useId();
  const [cleared, setCleared] = useState(false);
  const own = useOwnBlankGroups(product, {
    settings,
    savedEntry: product.ownBlankGroups,
    onSaved: () => setCleared(true),
  });
  return (
    <div className="flex flex-col gap-2 border-t pt-4" data-testid="own-blanks-byo-clear">
      {!cleared && (
        <>
          <HeldBackNote>{t('ownBlanks.byoClear')}</HeldBackNote>
          <Button
            type="button"
            variant="outline"
            className="h-8 w-fit gap-1.5 px-3 text-xs motion-reduce:transition-none"
            aria-busy={own.saving || undefined}
            aria-describedby={own.refusalMessage ? refusalId : undefined}
            onClick={() => {
              if (own.saving) return;
              void own.clear();
            }}
          >
            {own.saving && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {t('supply.groups.close')}
          </Button>
          {own.refusalMessage && (
            <p
              id={refusalId}
              role="alert"
              className="flex items-start gap-1 text-xs leading-normal text-destructive"
              data-testid="own-blanks-refusal"
            >
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              <span>{own.refusalMessage}</span>
            </p>
          )}
        </>
      )}
      <div role="status" aria-live="polite">
        {cleared && (
          <NoteLine icon={CheckCircle2} className="leading-normal">
            {t('ownBlanks.cleared')}
          </NoteLine>
        )}
      </div>
    </div>
  );
}

type Editing = { split: GarmentSplit | null; focusSwitch: boolean };

/** A section that takes focus when the editor opens: the shadcn ring on keyboard focus only (DESIGN.md §16). */
const SECTION_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

function OwnBlankGroupsSection({
  product,
  settings,
  onMapIt,
}: {
  product: PreviewProduct;
  settings: ProductSyncSettings;
  onMapIt: (table: CodeTable, name?: string) => void;
}) {
  const { t } = useTranslation('productSync');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [notice, setNotice] = useState<'saved' | 'cleared' | null>(null);
  // What the last save answered, shown until the preview refetch brings the
  // product's own entry (a new object on that path).
  const [local, setLocal] = useState<{ entry: OwnBlankValidEntry | null; from: OwnBlankSavedEntry | undefined } | null>(
    null,
  );
  const savedEntry: OwnBlankSavedEntry | undefined =
    local && local.from === product.ownBlankGroups ? (local.entry ?? undefined) : product.ownBlankGroups;

  const options = useMemo(() => ownGarmentOptions(settings, product), [settings, product]);
  const splittable = useMemo(() => ownSplittable(product, settings), [product, settings]);
  const stale = isOwnEntryStale(savedEntry, product, settings, options);
  const valid = isValidOwnEntry(savedEntry) ? savedEntry : null;

  // Focus goes back to the collapsed control when the editor closes.
  const collapsedRef = useRef<HTMLDivElement>(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    if (wasEditing.current && !editing) {
      const active = document.activeElement;
      if (active === null || active === document.body) collapsedRef.current?.querySelector<HTMLElement>('button')?.focus();
    }
    wasEditing.current = editing !== null;
  }, [editing]);

  if (!editing && !savedEntry && !splittable.colour && !splittable.size) return null;

  const open = (split: GarmentSplit | null, focusSwitch: boolean) => {
    setNotice(null);
    setEditing({ split, focusSwitch });
  };

  const onSaved = (saved: OwnBlankSaved) => {
    setLocal({ entry: saved.entry, from: product.ownBlankGroups });
    setNotice(saved.entry ? 'saved' : 'cleared');
    setEditing(null);
  };

  return (
    <div className="flex flex-col gap-3 border-t pt-4" data-testid="own-blanks-panel">
      {editing ? (
        <OwnBlankGroupsEditor
          product={product}
          settings={settings}
          savedEntry={savedEntry}
          stale={stale}
          initialSplit={editing.split}
          focusSwitch={editing.focusSwitch}
          splittable={splittable}
          onMapIt={onMapIt}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      ) : (
        <div ref={collapsedRef} className="flex flex-col gap-2">
          {stale ? (
            <>
              <HeldBackNote>{t('job.blocker.own_blank_groups_changed')}</HeldBackNote>
              <Button
                type="button"
                variant="outline"
                className="h-8 w-fit gap-1.5 px-3 text-xs motion-reduce:transition-none"
                onClick={() => open(null, false)}
              >
                {t('ownBlanks.change')}
              </Button>
            </>
          ) : valid ? (
            <OwnBlankSummary product={product} entry={valid} options={options} onChange={() => open(null, false)} />
          ) : (
            <SplitControl
              productId={`own-${product.id}`}
              split={null}
              splittable={splittable}
              onSplitChange={(split) => open(split, true)}
            />
          )}
        </div>
      )}
      {/* Always mounted, so the saved note is announced when it appears. */}
      <div role="status" aria-live="polite" data-testid="own-blanks-notice">
        {notice && !editing && (
          <NoteLine icon={CheckCircle2} className="leading-normal">
            {notice === 'saved' ? t('ownBlanks.saved') : t('ownBlanks.cleared')}
          </NoteLine>
        )}
      </div>
    </div>
  );
}

/** The saved choice: each garment with its store values, the default garment last ("every other colour"). */
function OwnBlankSummary({
  product,
  entry,
  options,
  onChange,
}: {
  product: PreviewProduct;
  entry: OwnBlankValidEntry;
  options: readonly OwnGarmentOption[];
  onChange: () => void;
}) {
  const { t } = useTranslation('productSync');
  const spelling = new Map(productValues(product, entry.split).map((v) => [garmentValueKey(v.value), v.value]));
  const titleOf = (code: string) => options.find((o) => o.code === code.toUpperCase())?.title ?? code.toUpperCase();
  const defaultCode = product.productType.code?.toUpperCase() ?? '';
  const headingId = useId();
  return (
    <div className="flex flex-col gap-2" data-testid="own-blanks-summary">
      <p id={headingId} className="text-sm font-medium">
        {t('ownBlanks.title')}{' '}
        <span className="font-normal text-muted-foreground">
          {entry.split === 'colour' ? t('supply.groups.perColour') : t('supply.groups.perSize')}
        </span>
      </p>
      <ul className="flex flex-col gap-1 text-xs" aria-labelledby={headingId}>
        {entry.groups.map((group) => (
          <li key={group.code} className="flex items-start gap-2">
            <Shirt className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 break-words leading-normal">
              {t('ownBlanks.summaryLine', {
                garment: titleOf(group.code),
                values: group.values.map((value) => spelling.get(garmentValueKey(value)) ?? value).join(', '),
              })}
            </span>
          </li>
        ))}
        <li className="flex items-start gap-2 text-muted-foreground">
          <Shirt className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="min-w-0 break-words leading-normal">
            {t('ownBlanks.summaryLine', {
              garment: titleOf(defaultCode),
              values: entry.split === 'colour' ? t('ownBlanks.rest.colour') : t('ownBlanks.rest.size'),
            })}
          </span>
        </li>
      </ul>
      <Button
        type="button"
        variant="outline"
        className="h-8 w-fit gap-1.5 px-3 text-xs motion-reduce:transition-none"
        onClick={onChange}
      >
        {t('ownBlanks.change')}
      </Button>
    </div>
  );
}

function OwnBlankGroupsEditor({
  product,
  settings,
  savedEntry,
  stale,
  initialSplit,
  focusSwitch,
  splittable,
  onMapIt,
  onClose,
  onSaved,
}: {
  product: PreviewProduct;
  settings: ProductSyncSettings;
  savedEntry: OwnBlankSavedEntry | undefined;
  stale: boolean;
  initialSplit: GarmentSplit | null;
  focusSwitch: boolean;
  splittable: { colour: boolean; size: boolean };
  onMapIt: (table: CodeTable, name?: string) => void;
  onClose: () => void;
  onSaved: (saved: OwnBlankSaved) => void;
}) {
  const { t } = useTranslation('productSync');
  const own = useOwnBlankGroups(product, { settings, savedEntry, initialSplit, onSaved });
  const { draft, options, defaultCode, lockedValues } = own;
  const split = draft.split;
  const headingId = useId();
  const assignId = useId();
  const assignLabelId = useId();
  const refusalId = useId();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [tooMany, setTooMany] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const others = options.filter((o) => o.code !== defaultCode);
  const optionByCode = useMemo(() => new Map(options.map((o) => [o.code, o])), [options]);

  useEffect(() => {
    setSelected(new Set());
    setTooMany(false);
  }, [split]);

  // The control that opened the editor is gone: from the link, focus follows
  // to the switch; from "Change base garments", to the editor itself.
  const rootRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const pressed = focusSwitch ? root.querySelector<HTMLElement>('button[aria-pressed="true"]') : null;
    (pressed ?? root).focus();
    // Mount only: where focus goes when the editor opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo<BlankValueMatchRow[]>(() => {
    if (!split) return [];
    return productValues(product, split).map(({ value, count }) => {
      const key = garmentValueKey(value);
      const group = draft.groups.find((g) => g.storeValues.some((v) => garmentValueKey(v) === key));
      const code = group?.garmentKey ?? defaultCode;
      const row: BlankValueMatchRow = {
        storeValue: value,
        variantCount: count,
        blankValue: null,
        how: 'auto',
        garment: { key: code, title: optionByCode.get(code)?.title ?? code, imageUrl: null },
      };
      return lockedValues.has(key) ? { ...row, locked: true } : row;
    });
  }, [split, product, draft.groups, defaultCode, optionByCode, lockedValues]);

  // Only one product type code, and nothing saved to clear: nowhere to move a value.
  if (others.length === 0 && !savedEntry) {
    return (
      <section
        ref={rootRef}
        tabIndex={-1}
        aria-labelledby={headingId}
        className={cn('flex flex-col items-start gap-2 rounded-md border border-dashed px-3 py-3', SECTION_FOCUS)}
        data-testid="own-blanks-empty"
      >
        <h4 id={headingId} className="text-sm font-medium">
          {t('ownBlanks.empty.title')}
        </h4>
        <p className="max-w-prose text-xs leading-normal text-muted-foreground">{t('ownBlanks.empty.body')}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
            onClick={() => onMapIt('productTypes')}
          >
            {t('ownBlanks.empty.cta')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
            onClick={onClose}
          >
            {t('ownBlanks.cancel')}
          </Button>
        </div>
      </section>
    );
  }

  const assign = (code: string) => {
    if (!split) return;
    const values = productValues(product, split)
      .map((entry) => entry.value)
      .filter((value) => selected.has(garmentValueKey(value)));
    const outcome = own.assign(values, code);
    setTooMany(outcome === 'too_many');
    if (outcome === 'assigned') {
      setSelected(new Set());
      setAnnouncement(
        t('supply.groups.assigned', { garment: optionByCode.get(code)?.title ?? code, count: values.length }),
      );
    }
  };

  return (
    <section
      ref={rootRef}
      tabIndex={-1}
      aria-labelledby={headingId}
      className={cn('flex flex-col gap-3 rounded-md', SECTION_FOCUS)}
      data-testid="own-blanks-editor"
    >
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      <h4 id={headingId} className="text-sm font-medium">
        {t('ownBlanks.title')}
      </h4>
      {stale && <HeldBackNote>{t('job.blocker.own_blank_groups_changed')}</HeldBackNote>}
      <SplitControl
        productId={`own-${product.id}`}
        split={split}
        splittable={splittable}
        locked={own.splitLocked}
        onSplitChange={(next) => {
          // Nothing saved: closing the switch is the same as Cancel.
          if (next === null && !savedEntry) onClose();
          else own.setSplit(next);
        }}
      />
      {split ? (
        <>
          <p className="max-w-prose text-xs leading-normal text-muted-foreground">
            {split === 'colour' ? t('supply.groups.hintColour') : t('supply.groups.hintSize')}
          </p>
          <div className="flex flex-wrap items-end gap-x-3 gap-y-2" data-testid="own-blanks-assign">
            <div className="flex w-full max-w-sm flex-col gap-1.5">
              <Label id={assignLabelId} htmlFor={assignId} className="text-sm font-medium">
                {t('supply.groups.assign')}
              </Label>
              <ValuePicker
                id={assignId}
                labelledBy={assignLabelId}
                options={options}
                value={null}
                placeholder={t('supply.groups.assignPlaceholder')}
                invalid={false}
                required={false}
                disabled={selected.size === 0}
                onChange={assign}
              />
            </div>
            <p className="pb-2 text-xs tabular-nums text-muted-foreground" aria-live="polite">
              {t('supply.groups.selected', { count: selected.size })}
            </p>
          </div>
          {tooMany && (
            <p className="text-xs leading-normal text-amber-700" role="alert">
              {t('supply.groups.tooMany', { max: GARMENT_GROUPS_MAX + 1 })}
            </p>
          )}
          <BlankValueMatchTable
            idPrefix={`own-blanks-${product.id}`}
            matches={split === 'colour' ? { colours: rows, sizes: [] } : { colours: [], sizes: rows }}
            blankValues={{ colours: [], sizes: [] }}
            designFor={() => false}
            ownerName=""
            onMatch={() => {}}
            onDesignChoice={() => {}}
            showDesignChoice={false}
            matchColumns={false}
            grouped
            selection={{ group: split, selected, onChange: setSelected }}
          />
        </>
      ) : (
        <p className="max-w-prose text-xs leading-normal text-muted-foreground" data-testid="own-blanks-clear-hint">
          {t('ownBlanks.clearHint')}
        </p>
      )}
      <div className="flex flex-col gap-2 border-t pt-3" data-testid="own-blanks-footer">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
            onClick={onClose}
          >
            {t('ownBlanks.cancel')}
          </Button>
          <Button
            type="button"
            className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none"
            // Never disabled while saving: focus stays, a second click is ignored.
            disabled={!own.dirty && !own.saving}
            aria-busy={own.saving || undefined}
            aria-describedby={own.refusalMessage ? refusalId : undefined}
            onClick={() => {
              if (own.saving) return;
              void own.save();
            }}
          >
            {own.saving && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {t('ownBlanks.save')}
          </Button>
        </div>
        {own.refusalMessage && (
          <p
            id={refusalId}
            role="alert"
            className="flex items-start gap-1 text-xs leading-normal text-destructive"
            data-testid="own-blanks-refusal"
          >
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            <span>{own.refusalMessage}</span>
          </p>
        )}
      </div>
    </section>
  );
}
